import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'

/**
 * Single generic marker CSS class. Any page opts a section into pushing the Salesforce embedded-messaging
 * chat bubble above itself purely by adding this class to the element that — in the page's CURRENT state —
 * is the active sticky section the chat must clear. No hook call or registration is needed.
 *
 * The class is declarative and style-free: no CSS rule anywhere keys off it (it is a JS selector only), and
 * it is additive (it sits alongside existing classes without changing layout/styles/positions/sizes). The
 * page is responsible for keeping EXACTLY ONE element marked at a time (see `product-details/index.tsx`),
 * though the controller tolerates N elements by taking the one that requires the largest lift.
 */
const STICKY_SECTION_MARKER_CLASS = 'cc-sticky-section'

/** Selector for the cross-origin chat button injected by the Commerce Cloud content asset. */
const BUBBLE_CHAT_QUERY_SELECTOR = 'button#embeddedMessagingConversationButton'

/** CSS custom property consumed by the chat rule in `globalStyles.ts`. */
const CHAT_BOTTOM_OFFSET_VAR = '--cc-chat-bottom-offset'

/** Vertical gap (px) left between the chat bubble and the sticky element above which it floats. */
const CHAT_STICKY_GAP = 8

/** Stop looking for the asynchronously-injected chat button after this many ms. */
const CHAT_OBSERVER_TIMEOUT = 5000

/** Throttle window (ms) for scroll/resize recomputes (mirrors the legacy SCROLL_TIMEOUT). */
const SCROLL_THROTTLE = 200

/**
 * Delay (ms) after a route change for one final re-measure.
 *
 * WHY: on a quiet destination page the sticky module may mount a few frames AFTER the route's rAF pass
 * (e.g. the Callimacus widget waits on page-designer data), with no body mutation to re-trigger the
 * MutationObserver. This single settle pass raises the (already-reset) offset once the real marker is in
 * place, without polling. The MutationObserver remains the safety net for anything that mounts later.
 */
const SETTLE_RECOMPUTE_DELAY = 300

/**
 * @name useStickyChatPosition
 *
 * @hook
 *
 * @description
 * Centralized, class-driven controller that keeps the Salesforce embedded-messaging chat bubble
 * positioned ABOVE (vertically, via a CSS custom property — not just z-index) the currently active
 * sticky section on the site, on BOTH desktop and mobile.
 *
 * Because the chat is injected by a Commerce Cloud content asset as a cross-origin widget (not a React
 * component), a React Context cannot reach it. This hook therefore operates at the DOM level: it scans
 * for elements marked with the {@link STICKY_SECTION_MARKER_CLASS} class, measures how high it must lift
 * the chat to clear the TOP edge of the one that needs the largest lift, and writes
 * `--cc-chat-bottom-offset` on `document.documentElement`. `globalStyles.ts` consumes that variable via
 * `bottom: calc(20px + var(--cc-chat-bottom-offset, 0px))`, so the theme stays the styling source of
 * truth and no inline `style.bottom` is written on the button.
 *
 * The controller is intentionally DUMB and GENERIC: no per-page heuristics, no fixed-px/viewport-fraction
 * thresholds, no clamps, no position checks. The ONLY per-page decision — WHICH element carries the marker
 * at a given moment — lives in the page (e.g. the PDP), which reads already-computed state.
 *
 * Mount it ONCE high in the tree (the app shell). It renders nothing.
 */
const useStickyChatPosition = (): void => {
    const {pathname} = useLocation()

    // Stable handles into the mount-once effect, so the route-change effect below can reset + trigger a
    // fresh pass WITHOUT re-registering the observers/listeners (which must stay set up once).
    const reMeasureRef = useRef<(() => void) | null>(null)
    const resetOffsetRef = useRef<(() => void) | null>(null)
    // The `[pathname]` effect runs once at mount too; skip the reset there so it doesn't wipe the
    // mount-once effect's initial measurement. Only real navigations (pathname changes) reset.
    const isFirstRouteRunRef = useRef(true)

    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return

        const root = document.documentElement

        const setOffset = (px: number) => {
            root.style.setProperty(CHAT_BOTTOM_OFFSET_VAR, `${px}px`)
        }

        /**
         * Measures the active sticky section(s) and writes the offset. The rule is purely geometric and
         * generic — never a fixed pixel/viewport threshold, a position check, or a clamp:
         *
         *   1. Find every visible `cc-sticky-section` element (`height > 0 && width > 0`, so
         *      `display:none`/unmounted markers are skipped). The page keeps exactly one active at a time,
         *      but we tolerate N by taking the one that needs the largest lift (`max`).
         *   2. Lift the chat just above that element's TOP edge on the Y axis:
         *      `offset = max(innerHeight - rect.top, 0)`, so the chat clears the WHOLE section (a tall band
         *      or a low bar alike). NOT clamped to any fixed px or viewport fraction — the element is marked
         *      ONLY while it must push the chat, so the lift is naturally bounded by what the page marks.
         *      Guard `rect.top > 0`: a marker scrolled past the top contributes 0.
         *   3. Nothing marked/visible → 0 (base 20px): homepage / PLP / any route with no sticky section.
         *
         * The soft animation between sections is the CSS transition on the chat `bottom` in `globalStyles`.
         */
        const recompute = () => {
            const vh = window.innerHeight
            let lift = 0
            document.querySelectorAll<HTMLElement>(`.${STICKY_SECTION_MARKER_CLASS}`).forEach((el) => {
                const rect = el.getBoundingClientRect()
                const isVisible = rect.height > 0 && rect.width > 0
                if (!isVisible) return
                // Scrolled past the top → ignore (guard rect.top > 0).
                if (rect.top <= 0) return
                const offset = Math.max(vh - rect.top, 0)
                if (offset > lift) lift = offset
            })

            setOffset(lift > 0 ? lift + CHAT_STICKY_GAP : 0)
        }

        // --- Throttled scroll/resize handler (leading + trailing, SCROLL_THROTTLE window). ---
        let throttleTimeout: ReturnType<typeof setTimeout> | null = null
        const onScrollOrResize = () => {
            if (throttleTimeout) return
            recompute()
            throttleTimeout = setTimeout(() => {
                throttleTimeout = null
                recompute()
            }, SCROLL_THROTTLE)
        }

        // --- Observers for marked sticky elements (added/removed, resized, visibility changes). ---
        const resizeObserver = new ResizeObserver(() => recompute())
        const intersectionObserver = new IntersectionObserver(() => recompute())
        const observed = new WeakSet<HTMLElement>()

        const observeMarkers = () => {
            document.querySelectorAll<HTMLElement>(`.${STICKY_SECTION_MARKER_CLASS}`).forEach((el) => {
                if (observed.has(el)) return
                observed.add(el)
                resizeObserver.observe(el)
                intersectionObserver.observe(el)
            })
        }

        // Viewport/layout changes (e.g. mobile browser chrome collapsing) that don't fire `resize`.
        resizeObserver.observe(root)

        // --- MutationObserver: detect the async chat button + marker add/remove, then recompute. ---
        let chatFound = false
        const mutationObserver = new MutationObserver(() => {
            if (!chatFound && document.querySelector(BUBBLE_CHAT_QUERY_SELECTOR)) {
                chatFound = true
            }
            observeMarkers()
            recompute()
        })
        mutationObserver.observe(document.body, {childList: true, subtree: true})

        // Stop searching for the chat button after a sane timeout, but keep reacting to marker changes.
        const chatTimeout = setTimeout(() => {
            chatFound = true
        }, CHAT_OBSERVER_TIMEOUT)

        window.addEventListener('scroll', onScrollOrResize, {passive: true})
        window.addEventListener('resize', onScrollOrResize)

        // Expose stable handles so the route-change effect can reset the offset and re-scan + recompute
        // on navigation without tearing down these observers/listeners.
        reMeasureRef.current = () => {
            observeMarkers()
            recompute()
        }
        resetOffsetRef.current = () => setOffset(0)

        // Initial pass (covers elements/chat already present on navigation).
        observeMarkers()
        recompute()

        return () => {
            reMeasureRef.current = null
            resetOffsetRef.current = null
            mutationObserver.disconnect()
            resizeObserver.disconnect()
            intersectionObserver.disconnect()
            window.removeEventListener('scroll', onScrollOrResize)
            window.removeEventListener('resize', onScrollOrResize)
            if (throttleTimeout) clearTimeout(throttleTimeout)
            clearTimeout(chatTimeout)
            setOffset(0)
        }
    }, [])

    /**
     * Route-reactive RESET-then-remeasure for client-side (SPA) navigation.
     *
     * The offset is a CSS var on `<html>`, which is never unmounted, so it PERSISTS across routes. The
     * mount-once effect's MutationObserver samples the DOM mid-reconciliation and offers no guaranteed
     * recompute once the destination has settled — so a large value written during the previous page
     * (e.g. the PDP band lift) can survive onto a quiet new page whose sticky module mounts
     * asynchronously (the Callimacus widget waits on page-designer data) or is absent/hidden. The chat
     * then stays stuck high (observed on the Homepage).
     *
     * The fix is to RESET first, then re-measure, keyed on `pathname`:
     *   1. Write offset = 0 SYNCHRONOUSLY on every navigation, so the chat drops to its base immediately
     *      and a stale value can never leak across a route change. The re-measure below only ever RAISES
     *      it again for a marker that is actually present.
     *   2. Re-measure on a double requestAnimationFrame (after the new DOM commits), then once more after
     *      a short settle delay to catch a marker that mounts a few frames later on an otherwise-quiet
     *      page (async widget content) without polling. The mount-once MutationObserver remains the
     *      long-tail safety net for markers that mount even later.
     *
     * Everything is driven by the ABSENCE/geometry of markers after the DOM settles — never by route
     * name — so a future sticky module on any route adapts automatically with zero per-page code.
     */
    useEffect(() => {
        if (typeof window === 'undefined') return

        // On the very first run (mount) the mount-once effect has already measured the current DOM — do
        // NOT reset, or we'd wipe that initial offset. Reset only on subsequent navigations.
        if (isFirstRouteRunRef.current) {
            isFirstRouteRunRef.current = false
            return
        }

        // 1. Immediate reset — the previous page's offset cannot survive this navigation.
        resetOffsetRef.current?.()

        // 2. Re-measure after the new DOM commits (two frames), then after a short settle delay.
        let raf2 = 0
        const raf1 = window.requestAnimationFrame(() => {
            reMeasureRef.current?.()
            raf2 = window.requestAnimationFrame(() => {
                reMeasureRef.current?.()
            })
        })
        const settleTimeout = setTimeout(() => {
            reMeasureRef.current?.()
        }, SETTLE_RECOMPUTE_DELAY)

        return () => {
            window.cancelAnimationFrame(raf1)
            if (raf2) window.cancelAnimationFrame(raf2)
            clearTimeout(settleTimeout)
        }
    }, [pathname])
}

export {useStickyChatPosition, STICKY_SECTION_MARKER_CLASS, CHAT_STICKY_GAP, CHAT_OBSERVER_TIMEOUT, SCROLL_THROTTLE}
